import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { PageHeader } from "../components/PageHeader";
import type { Store, StoreSection } from "../types";

interface StoreDetail {
  store: Store;
  sections: StoreSection[];
}

interface StoresResp {
  stores: Store[];
}

interface CopySectionsResult {
  copied: number;
  skipped: number;
  sourceStoreName: string;
}

export default function StoreDetailPage() {
  const { id = "" } = useParams();
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ["store", id],
    queryFn: () => api.get<StoreDetail>(`/api/stores/${id}`),
  });
  const storesQ = useQuery({
    queryKey: ["stores"],
    queryFn: () => api.get<StoresResp>("/api/stores"),
  });

  const [sectionName, setSectionName] = useState("");
  const [storeName, setStoreName] = useState("");
  const [copyFromStoreId, setCopyFromStoreId] = useState("");
  const [copyMessage, setCopyMessage] = useState<string | null>(null);

  const addSection = useMutation({
    mutationFn: (n: string) => api.post(`/api/stores/${id}/sections`, { name: n }),
    onSuccess: () => {
      setSectionName("");
      qc.invalidateQueries({ queryKey: ["store", id] });
    },
  });

  const renameStore = useMutation({
    mutationFn: (n: string) => api.patch(`/api/stores/${id}`, { name: n }),
    onSuccess: () => {
      setStoreName("");
      qc.invalidateQueries({ queryKey: ["store", id] });
      qc.invalidateQueries({ queryKey: ["stores"] });
    },
  });

  const renameSection = useMutation({
    mutationFn: ({ sid, name }: { sid: string; name: string }) =>
      api.patch(`/api/stores/sections/${sid}`, { name }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["store", id] }),
  });

  const deleteSection = useMutation({
    mutationFn: (sid: string) => api.delete(`/api/stores/sections/${sid}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["store", id] }),
  });

  const reorder = useMutation({
    mutationFn: (order: string[]) => api.put(`/api/stores/${id}/sections`, { order }),
    onMutate: (order) => {
      const prev = qc.getQueryData<StoreDetail>(["store", id]);
      if (prev) {
        const map = new Map(prev.sections.map((s) => [s.id, s]));
        qc.setQueryData<StoreDetail>(["store", id], {
          ...prev,
          sections: order.map((sid, i) => ({ ...(map.get(sid) as StoreSection), sortOrder: i })),
        });
      }
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(["store", id], ctx.prev);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ["store", id] }),
  });

  const copySections = useMutation({
    mutationFn: (sourceStoreId: string) =>
      api.post<CopySectionsResult>(`/api/stores/${id}/sections/copy`, { sourceStoreId }),
    onSuccess: (res) => {
      setCopyMessage(
        res.copied > 0
          ? `Zkopírováno ${res.copied} sekcí z obchodu „${res.sourceStoreName}“` +
              (res.skipped > 0 ? ` (${res.skipped} přeskočeno — už existují)` : "") +
              "."
          : `Žádná nová sekce — všechny z „${res.sourceStoreName}“ už v tomto obchodě jsou.`
      );
      setCopyFromStoreId("");
      qc.invalidateQueries({ queryKey: ["store", id] });
    },
    onError: () => setCopyMessage("Kopírování se nezdařilo."),
  });

  if (!data) return <p className="text-slate-500">Načítám…</p>;

  const otherStores = (storesQ.data?.stores ?? []).filter((s) => s.id !== id);

  const move = (idx: number, dir: -1 | 1) => {
    const next = [...data.sections];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    reorder.mutate(next.map((s) => s.id));
  };

  return (
    <div>
      <Link to="/obchody" className="text-sm text-slate-500 hover:underline">
        ← Zpět na seznam obchodů
      </Link>
      <PageHeader icon="🏪" title={data.store.name} description="Pořadí sekcí určí, jak se seřadí položky nákupního seznamu." />

      <div className="card card-body mb-6">
        <h2 className="font-semibold mb-3 flex items-center gap-2">
          <span aria-hidden>✏️</span>
          <span>Přejmenovat obchod</span>
        </h2>
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            className="input"
            placeholder={data.store.name}
            value={storeName}
            onChange={(e) => setStoreName(e.target.value)}
          />
          <button
            className="btn-secondary flex items-center gap-1"
            disabled={!storeName.trim() || renameStore.isPending}
            onClick={() => renameStore.mutate(storeName.trim())}
          >
            <span aria-hidden>💾</span>
            <span>Uložit</span>
          </button>
        </div>
      </div>

      <div className="card card-body mb-6">
        <h2 className="font-semibold mb-3 flex items-center gap-2">
          <span aria-hidden>➕</span>
          <span>Přidat sekci</span>
        </h2>
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            className="input"
            placeholder="např. Ovoce a zelenina"
            value={sectionName}
            onChange={(e) => setSectionName(e.target.value)}
          />
          <button
            className="btn-primary flex items-center gap-1"
            disabled={!sectionName.trim() || addSection.isPending}
            onClick={() => addSection.mutate(sectionName.trim())}
          >
            <span aria-hidden>➕</span>
            <span>Přidat sekci</span>
          </button>
        </div>
      </div>

      <div className="card card-body mb-6">
        <h2 className="font-semibold mb-3 flex items-center gap-2">
          <span aria-hidden>📋</span>
          <span>Kopírovat sekce z jiného obchodu</span>
        </h2>
        {otherStores.length === 0 ? (
          <p className="text-sm text-slate-500">Nemáte jiný obchod, ze kterého by šlo sekce zkopírovat.</p>
        ) : (
          <>
            <p className="text-sm text-slate-600 mb-3">
              Sekce se přidají na konec seznamu. Sekce se stejným názvem (bez ohledu na velikost písmen) se
              přeskočí.
            </p>
            <div className="flex flex-col sm:flex-row gap-2">
              <select
                className="input flex-1"
                value={copyFromStoreId}
                onChange={(e) => {
                  setCopyFromStoreId(e.target.value);
                  setCopyMessage(null);
                }}
              >
                <option value="">— vyberte zdrojový obchod —</option>
                {otherStores.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="btn-secondary flex items-center gap-1"
                disabled={!copyFromStoreId || copySections.isPending}
                onClick={() => copySections.mutate(copyFromStoreId)}
              >
                <span aria-hidden>📋</span>
                <span>{copySections.isPending ? "Kopíruji…" : "Kopírovat sekce"}</span>
              </button>
            </div>
            {copyMessage && (
              <p
                className={`text-sm mt-3 ${copyMessage.includes("nezdařilo") ? "text-red-600" : "text-green-800"}`}
              >
                {copyMessage}
              </p>
            )}
          </>
        )}
      </div>

      <div className="card card-body">
        <h2 className="font-semibold mb-3 flex items-center gap-2">
          <span aria-hidden>🧭</span>
          <span>Sekce v pořadí průchodu obchodem</span>
        </h2>
        {data.sections.length === 0 ? (
          <p className="text-sm text-slate-500">Zatím tu nejsou žádné sekce.</p>
        ) : (
          <ul className="divide-y">
            {data.sections.map((s, idx) => (
              <SectionRow
                key={s.id}
                section={s}
                idx={idx}
                total={data.sections.length}
                onMove={move}
                onRename={(n) => renameSection.mutate({ sid: s.id, name: n })}
                onDelete={() => {
                  if (confirm(`Smazat sekci "${s.name}"?`)) deleteSection.mutate(s.id);
                }}
              />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function SectionRow({
  section,
  idx,
  total,
  onMove,
  onRename,
  onDelete,
}: {
  section: StoreSection;
  idx: number;
  total: number;
  onMove: (idx: number, dir: -1 | 1) => void;
  onRename: (name: string) => void;
  onDelete: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(section.name);
  return (
    <li className="py-3 flex flex-col sm:flex-row sm:items-center gap-2">
      <div className="flex items-center gap-2">
        <button
          className="btn-ghost px-2"
          disabled={idx === 0}
          onClick={() => onMove(idx, -1)}
          aria-label="Posunout nahoru"
        >
          ↑
        </button>
        <button
          className="btn-ghost px-2"
          disabled={idx === total - 1}
          onClick={() => onMove(idx, 1)}
          aria-label="Posunout dolů"
        >
          ↓
        </button>
        <span className="text-xs text-slate-400 w-6">{idx + 1}.</span>
      </div>
      {editing ? (
        <div className="flex-1 flex gap-2">
          <input className="input" value={val} onChange={(e) => setVal(e.target.value)} />
          <button
            className="btn-primary"
            onClick={() => {
              onRename(val.trim());
              setEditing(false);
            }}
          >
            OK
          </button>
          <button className="btn-ghost" onClick={() => setEditing(false)}>
            Zrušit
          </button>
        </div>
      ) : (
        <div className="flex-1 font-medium">{section.name}</div>
      )}
      <div className="flex gap-2">
        {!editing && (
          <button
            className="btn-secondary text-xs flex items-center gap-1"
            title="Přejmenovat sekci"
            onClick={() => {
              setVal(section.name);
              setEditing(true);
            }}
          >
            <span aria-hidden>✏️</span>
            <span>Přejmenovat</span>
          </button>
        )}
        <button className="btn-danger text-xs flex items-center gap-1" title="Smazat sekci" onClick={onDelete}>
          <span aria-hidden>🗑️</span>
          <span>Smazat</span>
        </button>
      </div>
    </li>
  );
}
